import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Plus, X, Mail, CheckCircle, Clock, Send, Loader2, Eye, RefreshCw } from 'lucide-react';
import api from '../../utils/api';
import { toast } from '../../hooks/use-toast';

const RecommendersForm = ({ applicationId, data, onSave }) => {
  const [recommenders, setRecommenders] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    relationship: 'Teacher',
    subject_taught: ''
  });

  useEffect(() => {
    if (applicationId) {
      loadRecommenders();
    }
  }, [applicationId]);

  const loadRecommenders = async () => {
    try {
      const response = await api.get(`/applications/${applicationId}/recommenders`);
      setRecommenders(response.data);
    } catch (error) {
      console.error('Error loading recommenders:', error);
    }
  };

  const handleAddRecommender = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Use the new endpoint that sends email
      await api.post(`/recommenders/request/${applicationId}`, formData);
      toast({ 
        title: 'Request Sent!', 
        description: `An email invitation has been sent to ${formData.email}` 
      });
      setFormData({ email: '', first_name: '', last_name: '', relationship: 'Teacher', subject_taught: '' });
      setShowForm(false);
      loadRecommenders();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to send recommendation request', 
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendRequest = async (recId, email) => {
    setResending(recId);
    try {
      await api.post(`/recommenders/resend/${recId}`);
      toast({ title: 'Reminder Sent!', description: `A reminder has been sent to ${email}` });
      loadRecommenders();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to send reminder', variant: 'destructive' });
    } finally {
      setResending(null);
    }
  };

  const getStatusIcon = (status) => {
    if (status === 'submitted') return <CheckCircle className="h-5 w-5 text-green-600" />;
    if (status === 'viewed') return <Eye className="h-5 w-5 text-blue-600" />;
    return <Clock className="h-5 w-5 text-yellow-600" />;
  };

  const getStatusBadge = (status) => {
    if (status === 'submitted') return <Badge className="bg-green-100 text-green-800">Submitted</Badge>;
    if (status === 'viewed') return <Badge className="bg-blue-100 text-blue-800">Viewed</Badge>;
    return <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-900">
          <strong>Requirements:</strong> Most colleges require 2 teacher recommendations and 1 counselor recommendation.
          When you add a recommender, they will receive an email with a secure link to submit their letter.
        </p>
      </div>

      {recommenders.map((rec, index) => (
        <Card key={index} className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h4 className="font-semibold text-lg">{rec.first_name} {rec.last_name}</h4>
                {getStatusBadge(rec.status)}
              </div>
              <p className="text-sm text-gray-600 mb-1">{rec.relationship}</p>
              <p className="text-sm text-gray-600 mb-3 flex items-center gap-1">
                <Mail className="h-3 w-3" />
                {rec.email}
              </p>
              {rec.email_sent_at && rec.status !== 'submitted' && (
                <p className="text-xs text-gray-500">
                  Invitation sent: {new Date(rec.email_sent_at).toLocaleDateString()}
                </p>
              )}
              {rec.submitted_at && (
                <p className="text-xs text-green-600">
                  Submitted: {new Date(rec.submitted_at).toLocaleDateString()}
                </p>
              )}
            </div>
            <div className="flex flex-col items-end gap-2">
              {getStatusIcon(rec.status)}
              {rec.status !== 'submitted' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleResendRequest(rec._id, rec.email)}
                  disabled={resending === rec._id}
                  className="text-xs"
                  data-testid={`resend-btn-${rec._id}`}
                >
                  {resending === rec._id ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <>
                      <RefreshCw className="h-3 w-3 mr-1" />
                      Resend
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </Card>
      ))}

      {!showForm && recommenders.length < 4 && (
        <Button type="button" onClick={() => setShowForm(true)} className="w-full" data-testid="add-recommender-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Recommender
        </Button>
      )}

      {showForm && (
        <Card className="p-6">
          <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <Send className="h-5 w-5 text-emerald-600" />
            Request Recommendation
          </h4>
          <form onSubmit={handleAddRecommender} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name *</Label>
                <Input 
                  value={formData.first_name} 
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})} 
                  required 
                  data-testid="rec-first-name"
                />
              </div>
              <div>
                <Label>Last Name *</Label>
                <Input 
                  value={formData.last_name} 
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})} 
                  required 
                  data-testid="rec-last-name"
                />
              </div>
            </div>
            <div>
              <Label>Email *</Label>
              <Input 
                type="email" 
                value={formData.email} 
                onChange={(e) => setFormData({...formData, email: e.target.value})} 
                required 
                placeholder="recommender@school.edu"
                data-testid="rec-email"
              />
              <p className="text-xs text-gray-500 mt-1">
                An invitation email will be sent to this address with a secure link
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Relationship *</Label>
                <Select value={formData.relationship} onValueChange={(value) => setFormData({...formData, relationship: value})}>
                  <SelectTrigger data-testid="rec-relationship"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Teacher">Teacher</SelectItem>
                    <SelectItem value="Counselor">Counselor</SelectItem>
                    <SelectItem value="Coach">Coach</SelectItem>
                    <SelectItem value="Employer">Employer</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.relationship === 'Teacher' && (
                <div>
                  <Label>Subject Taught</Label>
                  <Input 
                    value={formData.subject_taught} 
                    onChange={(e) => setFormData({...formData, subject_taught: e.target.value})} 
                    placeholder="e.g., Mathematics"
                    data-testid="rec-subject"
                  />
                </div>
              )}
            </div>
            <div className="flex space-x-2">
              <Button type="submit" className="flex-1" disabled={loading} data-testid="send-request-btn">
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Request
                  </>
                )}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </form>
        </Card>
      )}

      <div className="flex justify-end">
        <Button type="button" onClick={() => onSave(recommenders)} data-testid="save-continue-btn">Save & Continue</Button>
      </div>
    </div>
  );
};

export default RecommendersForm;